import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Switch,
  FormControlLabel, Snackbar, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';

const Profile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, userProfile, updateProfile } = useAuth();
  const { refreshUserDisplayNames } = useData();

  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (userProfile) {
      setDisplayName(userProfile.displayName || '');
      setPhone(userProfile.phoneNumber || userProfile.phone || '');
    }
  }, [userProfile]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSaveDisplayName = async () => {
    const trimmed = displayName.trim();
    if (trimmed && (trimmed.length < 2 || trimmed.length > 30)) {
      showSnackbar('Display name must be 2-30 characters.', 'error');
      return;
    }
    try {
      setSaving(true);
      await updateProfile({ displayName: trimmed || null });
      // Re-fetch the leaderboard name map so the new name shows without a reload
      refreshUserDisplayNames();
      showSnackbar('Display name updated!');
    } catch (error) {
      console.error('Error saving display name:', error);
      showSnackbar(`Failed to save: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSendCode = async () => {
    const trimmed = phone.trim();
    if (!trimmed) {
      showSnackbar('Enter a phone number first.', 'error');
      return;
    }
    try {
      setSendingCode(true);
      const sendCode = httpsCallable(getFunctions(), 'sendPhoneVerificationCode');
      await sendCode({ phoneNumber: trimmed });
      setOtpCode('');
      setOtpDialogOpen(true);
      showSnackbar('Verification code sent. Check your phone.');
    } catch (error) {
      console.error('Error sending verification code:', error);
      const msg = (error && error.message) || 'Could not send verification code.';
      showSnackbar(msg, 'error');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!/^\d{6}$/.test(otpCode)) {
      showSnackbar('Enter the 6-digit code from the text message.', 'error');
      return;
    }
    try {
      setVerifyingCode(true);
      const verifyCode = httpsCallable(getFunctions(), 'verifyPhoneCode');
      const result = await verifyCode({ code: otpCode });
      const data = result.data || {};
      if (data.success) {
        setOtpDialogOpen(false);
        setOtpCode('');
        // Optimistically update local profile state; the Cloud Function has
        // already persisted these fields to Firestore.
        if (data.phoneNumber) {
          setPhone(data.phoneNumber);
        }
        showSnackbar('Phone number verified!');
      } else {
        showSnackbar(data.message || 'Verification failed.', 'error');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      const msg = (error && error.message) || 'Could not verify code.';
      showSnackbar(msg, 'error');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleToggleSmsPreference = async (key, value) => {
    const next = {
      ...(userProfile?.smsPreferences || {}),
      [key]: value,
    };
    try {
      await updateProfile({ smsPreferences: next });
    } catch (error) {
      console.error('Error saving notification preference:', error);
      showSnackbar(`Failed to save preference: ${error.message}`, 'error');
    }
  };

  const containerStyle = {
    marginTop: isMobile ? '80px' : '128px',
    padding: isMobile ? '8px' : '20px',
  };

  const phoneVerified = !!userProfile?.phoneVerified;
  const optedOut = !!userProfile?.smsOptOut;

  return (
    <Container maxWidth="sm" style={containerStyle}>
      <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        My Profile
      </Typography>

      {/* Email (read-only) */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Email
        </Typography>
        <Typography variant="body1">
          {currentUser?.email}
        </Typography>
      </Paper>

      {/* Display Name */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Display Name
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This name will be shown on the leaderboard and picks instead of your email.
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter a display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          inputProps={{ maxLength: 30 }}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSaveDisplayName}
          disabled={displayName.trim() === (userProfile?.displayName || '')}
          loading={saving}
        >
          Save Display Name
        </Button>
      </Paper>

      {/* Phone Number */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Phone Number
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add your phone number to receive SMS notifications about game updates.
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="+1 (801) 555-1234"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={phoneVerified || sendingCode}
          />
          {phoneVerified ? (
            <CheckCircleIcon sx={{ color: '#2e7d32' }} />
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={handleSendCode}
              disabled={!phone.trim()}
              loading={sendingCode}
            >
              Verify
            </Button>
          )}
        </div>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}>
          By clicking Verify, you consent to receive a one-time SMS verification code at this number
          from The Big Board. After verification, you can choose which notifications to enable
          (new-week game alerts, pick reminders, and leaderboard updates). Message frequency varies.
          Message and data rates may apply. Reply STOP to opt out or HELP for help.
          {' '}
          <RouterLink to="/terms" style={{ color: '#666', fontSize: 'inherit' }}>Terms of Service</RouterLink>
          {' | '}
          <RouterLink to="/privacy" style={{ color: '#666', fontSize: 'inherit' }}>Privacy Policy</RouterLink>
        </Typography>
      </Paper>

      {/* Notification Preferences */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          SMS Notifications
        </Typography>
        {!phoneVerified && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Verify your phone number to enable SMS notifications.
          </Typography>
        )}
        {phoneVerified && optedOut && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            You replied STOP to a message and have opted out of all SMS. Reply START to re-enable.
          </Typography>
        )}
        <Tooltip title={phoneVerified ? '' : 'Verify your phone number first'}>
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={userProfile?.smsPreferences?.newGames || false}
                  onChange={(e) => handleToggleSmsPreference('newGames', e.target.checked)}
                  disabled={!phoneVerified || optedOut}
                />
              }
              label="New games published"
            />
            <br />
            <FormControlLabel
              control={
                <Switch
                  checked={userProfile?.smsPreferences?.kickoffReminder || false}
                  onChange={(e) => handleToggleSmsPreference('kickoffReminder', e.target.checked)}
                  disabled={!phoneVerified || optedOut}
                />
              }
              label="Picks reminder (2hrs before kickoff)"
            />
            <br />
            <FormControlLabel
              control={
                <Switch
                  checked={userProfile?.smsPreferences?.leaderboardUpdate || false}
                  onChange={(e) => handleToggleSmsPreference('leaderboardUpdate', e.target.checked)}
                  disabled={!phoneVerified || optedOut}
                />
              }
              label="Leaderboard updates"
            />
          </div>
        </Tooltip>
      </Paper>

      {/* OTP Entry Dialog */}
      <Dialog
        open={otpDialogOpen}
        onClose={() => !verifyingCode && setOtpDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Verification Code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We sent a 6-digit code to {phone}. Enter it below to verify your phone number.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            size="small"
            placeholder="123456"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 6,
              style: { letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.25rem' },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOtpDialogOpen(false)}
            disabled={verifyingCode}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleVerifyCode}
            disabled={otpCode.length !== 6}
            loading={verifyingCode}
          >
            Verify Code
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;
